<style lang="scss" scoped>
  .searchbar {
    z-index: 2;
    position: fixed;
    top: 64px;
    width: 100%;
    padding: 0px 16px;
    form {
      width: 100%;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      background-color: $not-white;
      padding-top: 24px;
      gap: 16px;
      button, .program-field, .location-field {
        width: 100%;
      }
      /deep/ .distance-dropdown {
        flex-grow: 1;
        width: 192px;
        .items {
          width: calc(100vw - 32px);
        }
      }
      .search-button {
        flex-grow: 1;
        width: 120px;
      }
    }

    @media screen and (min-width: $breakpoint-sm) {
      top: 64px;
      padding: 0px 32px;
      form {
        padding-top: 32px;
        .distance-dropdown, .search-button {
          flex: 1 1;
        }
        /deep/ .distance-dropdown .items {
          width: 100%;
        }
        /deep/ .program-field .items {
          width: 376px;
        }
        /deep/ .location-field .items {
          width: calc(50% - 8px);
        }
      }
    }

    @media screen and (min-width: $breakpoint-md) {
      top: 80px;
      form {
        .program-field, .location-field {
          flex: 1 1 40%;
        }
        .program-field, .location-field, .distance-dropdown, .search-button {
          max-width: 400px;
        }
        /deep/ .location-field .items {
          width: 100%;
        }
      }
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: 0px 40px;
      form {
        padding-top: 40px;
        .program-field, .location-field, .distance-dropdown {
          min-width: 400px;
        }
        /deep/ .program-field .items {
          width: 100%;
        }
      }
    }

    @media screen and (min-width: $breakpoint-xl) {
      left: $side-nav-default-width;
      width: calc(100% - (#{$side-nav-default-width}));
      form {
        flex-wrap: nowrap;
        .program-field, .location-field, .distance-dropdown {
          max-width: unset;
          min-width: unset;
          flex: 2 1 284px;
        }
        /deep/ .program-field .items {
          width: 376px;
        }
        .search-button {
          max-width: unset;
          flex: 1 1 auto;
        }
      }
    }

    @media screen and (min-width: 1440px) {
      width: 1200px;
      left: 50%;
      padding: 0px;
      transform: translateX(-50%) translateX($side-nav-default-width / 2);
    }
  }
</style>
<template>
  <div class="searchbar">
    <form ref="search" @submit.prevent="checkIfValidSearch">
      <div class="program-field">
        <TypeToFilterDropdown
          :firstList="returnedPrograms"
          :secondList="returnedSkills"
          :thirdList="returnedFilters"
          :placeholder="'Program or Skill'"
          :firstListHeader="'Programs'"
          :secondListHeader="'Skills'"
          :thirdListHeader="'My Saved Filters'"
          :iconType="'search'"
          :hasExpanders="true"
          :startingValue="programOrSkill"
          @selected="(value, list) => {handleProgramSelect(value, list)}"
        />
      </div>
      <div class="location-field">
        <TypeToFilterDropdown
          :firstList="returnedLocations"
          :placeholder="'City or Zip code'"
          :iconType="'location'"
          :startingValue="cityOrZip"
          :hasExpanders="false"
          :hasError="hasError"
          @selected="value => {$emit('locationSelected', value)}"
          @error="handleLocationFocus"
        />
      </div>
      <div class="distance-dropdown">
        <Dropdown
          :startingValue="defaultDistance"
          :options="distances"
          :icon="true"
          :checkmark="true"
        />
      </div>
      <div class="search-button">
        <Button
          :text="'Search'"
          :disabled="isSearchDisabled() && !allowSearchClickEvents"
          :hasDisabledStyle="allowSearchClickEvents && !cityOrZip"
          @clicked="checkForMissingLocation()"
        />
      </div>
      <ActionToast
        id="database-error-dialog"
        v-if="hasDatabaseError"
        :show="hasDatabaseError"
        :maxWidth="332"
        anchorPosition="bottom right"
        selfPosition="top right"
        priority="error"
        :buttonPrimaryErrorText="$t('tryAgain')"
        :buttonSecondaryErrorText="$t('contactUs')"
        @buttonPrimaryClicked="$emit('searchInitiated')"
      >
        <span>{{ $t('thereIsAProblemLoadingYourResults') }}</span>
      </ActionToast>
    </form>
  </div>
</template>

<script>
import Vue from 'vue'
import Dropdown from '@/components/atoms/Dropdown.vue'
import TypeToFilterDropdown from '@/components/atoms/TypeToFilterDropdown.vue'
import Button from '@/components/atoms/Button.vue'
import ActionToast from '@/components/atoms/ActionToast.vue'
import { MdCheckbox } from 'vue-material/dist/components'

Vue.use(MdCheckbox)

export default Vue.extend({
  name: 'Searchbar',
  components: {
    ActionToast,
    Button,
    Dropdown,
    TypeToFilterDropdown
  },
  props: {
    cityOrZipProp: String,
    programOrSkillProp: String,
    hasErrorProp: Boolean,
    hasDatabaseErrorProp: Boolean
  },
  beforeMount () {
    this.cityOrZip = this.cityOrZipProp
    this.programOrSkill = this.programOrSkillProp
    this.hasError = this.hasErrorProp
    this.hasDatabaseError = this.hasDatabaseErrorProp
  },
  data: () => ({
    listSelectedFrom: '',
    cityOrZip: '',
    programOrSkill: '',
    hasError: false,
    hasDatabaseError: false,
    allowSearchClickEvents: false,
    defaultDistance: 'Within 15 miles',
    distances: Vue.Constants.DISTANCES,
    returnedLocations: [
      {
        id: 1,
        name: 'Salt Lake City, UT'
      },
      {
        id: 2,
        name: 'Orem, UT'
      },
      {
        id: 3,
        name: 'Lehi, UT'
      },
      {
        id: 4,
        name: 'Sandy, UT'
      },
      {
        id: 5,
        name: 'Salt Lake County, UT'
      },
      {
        id: 6,
        name: 'San Diego, CA'
      },
      {
        id: 7,
        name: 'Sacramento, CA'
      },
      {
        id: 8,
        name: 'San Antonio, TX'
      },
      {
        id: 9,
        name: 'San Jose, CA'
      },
      {
        id: 10,
        name: 'San Francisco, CA'
      },
      {
        id: 11,
        name: 'Saratoga Springs, UT'
      },
      {
        id: 12,
        name: 'San Bernardino, CA'
      }
    ],
    // mocked returned programs for now so I can test the program or skill search field
    returnedPrograms: [
      {
        id: 1,
        name: 'Medical Administrative Assistant'
      },
      {
        id: 2,
        name: 'Medical Assistant'
      },
      {
        id: 3,
        name: 'Medical Coding and Billing'
      },
      {
        id: 4,
        name: 'Medical Laboratory Technician'
      },
      {
        id: 5,
        name: 'Medical Receptionist'
      },
      {
        id: 6,
        name: 'Medical Social Worker'
      },
      {
        id: 7,
        name: 'Care Coordinator'
      },
      {
        id: 8,
        name: 'Certified Nursing Assistant'
      },
      {
        id: 9,
        name: 'Patient Care Technician'
      }
    ],
    returnedSkills: [
      {
        id: 1,
        name: 'Electronic Medical Records'
      },
      {
        id: 2,
        name: 'Medical Knowledge'
      },
      {
        id: 3,
        name: 'Medical Equipment'
      },
      {
        id: 4,
        name: 'Basic Life Support'
      },
      {
        id: 5,
        name: 'Phlebotomy'
      },
      {
        id: 6,
        name: 'Medical Wizardry'
      }
    ],
    returnedFilters: [
      {
        id: 1,
        name: 'Medical Assistant + Hemodialysis'
      },
      {
        id: 2,
        name: 'Medical Records'
      }
    ]
  }),
  watch: {
    cityOrZipProp: function (newVal) {
      this.cityOrZip = newVal
    },
    programOrSkillProp: function (newVal) {
      this.programOrSkill = newVal
    },
    hasErrorProp: function (newVal) {
      this.hasError = newVal
    },
    hasDatabaseErrorProp: function (newVal) {
      this.hasDatabaseError = newVal
    }
  },
  methods: {
    isSearchDisabled () {
      if (!this.programOrSkill || !this.cityOrZip) {
        return true
      }
      return false
    },
    async checkIfValidSearch () {
      if (!this.isSearchDisabled()) {
        this.$emit('searchInitiated')
      }
    },
    checkForMissingLocation () {
      if (!this.cityOrZip && this.programOrSkill) {
        this.$emit('showLocationErrorText', true)
      } else {
        this.$emit('showLocationErrorText', false)
      }
    },
    handleProgramSelect (value, list) {
      this.listSelectedFrom = list
      this.$emit('programSelected', value)

      if (this.listSelectedFrom === 'firstList') {
        this.$emit('searchTypeSelected', 'program')
      } else if (this.listSelectedFrom === 'secondList') {
        this.$emit('searchTypeSelected', 'skill')
      } else if (this.listSelectedFrom === 'thirdList') {
        this.$emit('searchTypeSelected', 'filter')
      }
      if (!this.cityOrZip) {
        this.allowSearchClickEvents = true
      } else {
        this.allowSearchClickEvents = false
      }
    },
    handleLocationFocus (element) {
      element.focus()
    }
  }
})
</script>
